import React from "react";

import {Listbox, ListboxButton, ListboxOption, ListboxOptions} from "@headlessui/react";
import {ChevronUpDownIcon} from "@heroicons/react/16/solid";
import {CheckIcon} from "@heroicons/react/20/solid";
import Decimal from "decimal.js";
import {produce} from "immer";

import {
    LATE_MEAL_HOUR,
    deriveMealHour,
    generateTimeOptions,
    hourToName,
} from "@client/features/journal";
import * as orm from "@client/features/orm";

import * as analytics from "@client/shared/analytics";
import * as typography from "@client/shared/typography";
import {formatUnit} from "@client/utils";

import {
    EntryLike,
    calculateEntryMetrics,
    calculateMealMetrics,
    getMetrics,
    getRoundedCalculations,
} from "@client/calculations";
import {DoubleWidget} from "@client/components/DoubleWidget";
import {EntryCard, Props as EntryCardProps} from "@client/components/EntryCard";
import {EntryMetrics} from "@client/components/EntryMetrics";
import {Flush} from "@client/components/Flush";
import {MealSelector, getMealOptionsForDay} from "@client/components/MealSelector";
import {DecimalInput, Label, QuickSelect} from "@client/forms";
import * as widgets from "@client/forms/widgets";
import * as hardware from "@client/hardware";
import * as forms from "@client/site/forms";
import {useAppDispatch, useAppSelector} from "@client/store";
import {classNames, server} from "@reactivated";

const styles = {
    quantityUnitAndWholeThing: "stage-quantity flex flex-col md:flex-row",
} as const;

interface StageState<TEntry extends EntryLike> {
    method: "now" | "cupboard";
    values: Record<
        UUID,
        {quantity?: string | null; unit?: string | null; isRemoved?: boolean} | null
    >;
    entries: TEntry[];
    // Prediction selections state - normalized with entries
    predictionSelection?: {
        rootLocator: string; // The root entry we're predicting from
        selectedPatternHash: string | null; // Currently selected pattern/column
        // Selected entries are derived from entries array, not stored separately
    };
}

interface Props<TEntry extends EntryLike> {
    cardComponent?: React.ComponentType<EntryCardProps>;
    divideByServings?: DecimalValue;
    entries: StageState<TEntry>["entries"];
    extra?: (handler: {uuid: UUID; isRemoved: boolean}) => React.ReactNode;
    target:
        | {
              type: "RECIPE";
              recipe: server.journal.nutrition.Recipe.output;
          }
        | {
              type: "DAY";
              day: server.journal.nutrition.Day.output;
              currentMeal?: server.journal.nutrition.StagedMeal.output;
          }
        | null;
    preSelectedMealName?: string;
}

export interface Handler<TEntry extends EntryLike> {
    entryMetrics: React.ReactNode;
    mealSelector: React.ReactNode;
    hasErrors: boolean;
    data: StageState<TEntry>;
    entries: React.ReactNode;
    ingredients: React.ReactNode;
    singleEntry: React.ReactNode;
    bulkEntries: React.ReactNode;
    validateMeal: () => false | server.journal.nutrition.StagedMeal.output;
    validate: () => {
        meal: server.journal.nutrition.StagedMeal.output;
        entries: TEntry[];
        single: TEntry;
    } | null;
    calculations: ReturnType<typeof getRoundedCalculations>;
    setEntries: (entries: TEntry[]) => void;
    syncEntries: (entries: TEntry[]) => void;
    addEntry: (entry: TEntry) => void;
    removeEntry: (entry: TEntry) => void;
    setMethod: (method: "now" | "cupboard") => void;
    entriesWithValues: TEntry[];
    bulkEntriesContent: (filter: (entry: TEntry) => boolean) => React.ReactNode;
    // Prediction selection handlers
    setPredictionSelection: (
        selection: StageState<TEntry>["predictionSelection"],
    ) => void;
    updatePredictionPattern: (hash: string | null) => void;
}

interface MealEditorState {
    mealMode: "MANUAL" | "AUTO_CREATED";
    suggestedHour: number;
    manualMealName: string | null;
    error?: string | null;
}

interface MealEditorHandler {
    mealSelector: React.ReactNode;
    validateMeal: () => false | server.journal.nutrition.StagedMeal.output;
    state: MealEditorState;
    setState: React.Dispatch<React.SetStateAction<MealEditorState>>;
    error?: string | null;
}

interface MealEditorProps {
    target:
        | {
              type: "RECIPE";
              recipe: server.journal.nutrition.Recipe.output;
          }
        | {
              type: "DAY";
              day: server.journal.nutrition.Day.output;
              currentMeal?: server.journal.nutrition.StagedMeal.output;
          }
        | null;
    method?: "now" | "cupboard";
    preSelectedMealName?: string;
}

export function useMealEditor(props: MealEditorProps): MealEditorHandler {
    const dispatch = useAppDispatch();
    const profile = useAppSelector(orm.getProfile);

    const defaultMeal =
        props.target?.type == "DAY"
            ? (props.target.day.meals
                  .filter((m) => m.name != "__SUPPLEMENTS__")
                  .filter((m) => m.type != "AUTO_CREATED")
                  .at(-1)?.name ??
              getMealOptionsForDay(props.target.day).allMeals[0]?.value)
            : null;

    const mealName =
        props.target?.type == "DAY"
            ? (props.target.currentMeal?.name ?? defaultMeal)
            : null;

    const initialMealMode =
        (props.target?.type == "DAY" ? props.target.currentMeal?.type : null) ??
        profile?.settings.meals ??
        "MANUAL";

    const initialHour =
        props.target?.type != "DAY"
            ? new Date().getHours()
            : ((props.target.currentMeal?.type == "AUTO_CREATED"
                  ? props.target.currentMeal.hour
                  : null) ?? deriveMealHour(new Date(), props.target.day.key));

    const [state, setState] = React.useState<MealEditorState>({
        suggestedHour: initialHour,
        mealMode: initialMealMode,
        manualMealName: mealName,
        error: null,
    });

    const changeMealMode = () => {
        const nextMode = state.mealMode == "AUTO_CREATED" ? "MANUAL" : "AUTO_CREATED";
        setState((prev) => ({...prev, mealMode: nextMode}));

        void analytics.track("CHANGE_MEAL_MODE", {
            meal_mode: nextMode,
        });

        if (profile == null) {
            return;
        }

        const updatedProfile = produce(profile, (draft) => {
            draft.settings.meals = nextMode;
        });
        dispatch({type: "SET_PROFILE", profile: updatedProfile});
        void server.core.rpc.update_settings(updatedProfile.settings);
    };

    const changeStagedMeal = (meal: string | null) => {
        setState((prev) => ({...prev, manualMealName: meal}));
    };

    const changeAutomaticHour = (hour: number) => {
        setState((prev) => ({...prev, suggestedHour: hour}));
    };

    const timeOptions = generateTimeOptions().map((option) => {
        return {
            ...option,
            entriesCount:
                props.target?.type === "DAY" &&
                props.target.day.meals.find(
                    (meal) =>
                        meal.type === "AUTO_CREATED" && meal.hour === option.value,
                )?.entries.length,
        };
    });

    const selectedTime =
        timeOptions.find((opt) => opt.value === state.suggestedHour) || timeOptions[0];

    const useNames = state.mealMode == "MANUAL";

    const mealSelector =
        props.preSelectedMealName == null &&
        props.method !== "cupboard" &&
        props.target?.type == "DAY" &&
        (state.mealMode == "AUTO_CREATED" ? (
            <div className="py-1">
                <div className="flex items-center justify-center text-lg text-gray-800">
                    <div className="flex items-center">
                        <div className="font-medium">Logged at</div>
                        <Listbox
                            aria-label="Meal time"
                            value={selectedTime}
                            onChange={(option) => changeAutomaticHour(option.value)}
                        >
                            <div className="relative ml-2">
                                <ListboxButton
                                    // This needs to be onTouchStart or won't work on iOS.
                                    onTouchStart={() => {
                                        void hardware.haptic();
                                        setTimeout(() => {
                                            const selectedElement =
                                                document.querySelector(
                                                    `[data-scroll-value="${selectedTime.value}"]`,
                                                );
                                            if (selectedElement) {
                                                selectedElement.scrollIntoView({
                                                    behavior: "smooth",
                                                    block: "center",
                                                });
                                            }
                                        }, 0);
                                    }}
                                    className="
                                        grid w-24 cursor-default grid-cols-1 rounded-md
                                        bg-white px-2 py-1 text-left text-gray-900
                                        outline-1 -outline-offset-1 outline-gray-300
                                        outline-solid
                                        focus-visible:outline-2
                                        focus-visible:-outline-offset-2
                                        focus-visible:outline-indigo-600
                                        focus-visible:outline-solid
                                    "
                                >
                                    <span
                                        className="
                                            col-start-1 row-start-1 truncate pr-4
                                            text-base font-semibold
                                        "
                                    >
                                        {selectedTime.label}
                                        {selectedTime.value >= LATE_MEAL_HOUR && (
                                            <sup
                                                aria-label="next day"
                                                className="
                                                    text-[10px] font-semibold
                                                    text-indigo-500
                                                "
                                            >
                                                +1
                                            </sup>
                                        )}
                                    </span>
                                    <ChevronUpDownIcon
                                        aria-hidden="true"
                                        className="
                                            col-start-1 row-start-1 size-5 self-center
                                            justify-self-end text-gray-500
                                            sm:size-4
                                        "
                                    />
                                </ListboxButton>
                                <ListboxOptions
                                    anchor="bottom"
                                    transition
                                    className={classNames(
                                        "absolute z-10 mt-1 [--anchor-max-height:15rem] max-h-60 w-48 overflow-auto rounded-md bg-white py-1 text-base shadow-lg outline-solid outline-1 outline-black/5 data-closed:data-leave:opacity-0 data-leave:transition data-leave:duration-100 data-leave:ease-in sm:text-sm",
                                        // open == false && "hidden",
                                    )}
                                >
                                    {timeOptions.map((option) => (
                                        <React.Fragment key={option.value}>
                                            {option.value === LATE_MEAL_HOUR && (
                                                <div
                                                    className="
                                                        mx-3 mt-2 mb-1 border-t
                                                        border-stone-200 pt-2
                                                        text-[10px] font-medium
                                                        tracking-widest text-gray-400
                                                        uppercase select-none
                                                    "
                                                >
                                                    After midnight
                                                </div>
                                            )}
                                            <ListboxOption
                                                value={option}
                                                onClick={() => {
                                                    void hardware.haptic();
                                                }}
                                                data-scroll-value={option.value}
                                                className="
                                                    group relative cursor-default py-2
                                                    pr-9 pl-3 text-gray-900 select-none
                                                    data-focus:bg-indigo-600
                                                    data-focus:text-white
                                                    data-focus:outline-hidden
                                                "
                                            >
                                                <span
                                                    className="
                                                        block truncate font-normal
                                                        group-data-selected:font-semibold
                                                    "
                                                >
                                                    {option.label}
                                                    {option.entriesCount != null && (
                                                        <span
                                                            className="
                                                                text-xs font-normal
                                                                text-gray-500
                                                                group-data-active:text-white
                                                            "
                                                        >
                                                            <span className="mx-2">
                                                                •
                                                            </span>
                                                            {option.entriesCount}{" "}
                                                            {option.entriesCount == 1
                                                                ? "entry"
                                                                : "entries"}
                                                        </span>
                                                    )}
                                                </span>
                                                <span
                                                    className="
                                                        absolute inset-y-0 right-0 flex
                                                        items-center pr-4
                                                        text-indigo-600
                                                        group-data-focus:text-white
                                                        group-[:not([data-selected])]:hidden
                                                    "
                                                >
                                                    <CheckIcon
                                                        aria-hidden="true"
                                                        className="size-5"
                                                    />
                                                </span>
                                            </ListboxOption>
                                        </React.Fragment>
                                    ))}
                                    <div className="sticky inset-0 z-50">
                                        <div
                                            className="
                                                relative -bottom-1 cursor-default
                                                border-b border-blue-100 bg-blue-50
                                                py-1.5 pr-1 pl-3 font-semibold
                                                text-gray-700 select-none
                                                data-focus:bg-indigo-600
                                                data-focus:text-white
                                            "
                                        >
                                            <button
                                                onClick={() => changeMealMode()}
                                                className="
                                                    flex w-full items-center
                                                    justify-between transition-colors
                                                    hover:bg-blue-100
                                                "
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="text-left">
                                                        <p
                                                            className="
                                                                text-sm font-normal
                                                                text-gray-800
                                                            "
                                                        >
                                                            Named meals
                                                        </p>
                                                    </div>
                                                </div>
                                                <div
                                                    className={`
                                                        h-6 w-11 scale-75 rounded-full
                                                        transition-colors
                                                        ${
                                                            useNames
                                                                ? "bg-blue-600"
                                                                : "bg-gray-300"
                                                        }
                                                    `}
                                                >
                                                    <div
                                                        className={`
                                                            size-5 transform
                                                            rounded-full bg-white
                                                            transition-transform
                                                            ${
                                                                useNames
                                                                    ? `translate-x-5`
                                                                    : `translate-x-0.5`
                                                            }
                                                            mt-0.5 ml-0.5
                                                        `}
                                                    />
                                                </div>
                                            </button>
                                        </div>
                                    </div>
                                </ListboxOptions>
                            </div>
                        </Listbox>
                    </div>
                </div>
            </div>
        ) : props.target.type == "DAY" ? ( // eslint-disable-line @typescript-eslint/no-unnecessary-condition
            <MealSelector
                day={props.target.day}
                onAuto={changeMealMode}
                onMealChange={changeStagedMeal}
                value={state.manualMealName}
                error={state.error ?? null}
            />
        ) : null);

    const validateMeal = (): false | server.journal.nutrition.StagedMeal.output => {
        if (props.preSelectedMealName != null) {
            return {name: props.preSelectedMealName, type: "MANUAL", hour: null};
        }
        if (state.mealMode == "AUTO_CREATED") {
            return {
                name: hourToName(state.suggestedHour),
                type: state.mealMode,
                hour: state.suggestedHour,
            };
        }
        if (
            props.method != "cupboard" &&
            props.target?.type == "DAY" &&
            (state.manualMealName == null || state.manualMealName == "")
        ) {
            setState((prev) => ({...prev, error: "A meal name is required"}));
            return false;
        }

        return {name: state.manualMealName ?? "", type: state.mealMode, hour: null};
    };

    return {
        mealSelector,
        validateMeal,
        state,
        setState,
    };
}

export function useStage<
    TEntry extends EntryLike = server.journal.nutrition.InlineEntry.output,
>(props: Props<TEntry>): Handler<TEntry> {
    const useKilojoules = forms.useUseKilojoules();
    const hasHiddenMacros = forms.useHasHiddenMacros();

    const [errors, setErrors] = React.useState<{
        meal?: string | null;
        quantity?: string | null;
    }>({});

    function getInitialValues(entries: Props<TEntry>["entries"]) {
        return produce<StageState<TEntry>["values"]>({}, (draftState) => {
            for (const entry of entries) {
                draftState[entry.uuid] = {
                    quantity: entry.quantity,
                    unit: entry.unit,
                };

                for (const ingredient of entry.expanded_recipe) {
                    draftState[ingredient.uuid] = {
                        quantity: ingredient.quantity,
                        unit: ingredient.unit,
                    };
                }
            }
        });
    }

    const [stage, setStage] = React.useState<StageState<TEntry>>({
        method: "now",
        entries: props.entries,
        values: getInitialValues(props.entries),
        predictionSelection: undefined,
    });

    const mealEditor = useMealEditor({
        target: props.target,
        method: stage.method,
        preSelectedMealName: props.preSelectedMealName,
    });

    const hasErrors = Object.values(errors).some((error) => error != null);

    const setEntries = (entries: StageState<TEntry>["entries"]) => {
        // TODO: this won't work for setting entries that are recipes.
        setStage((prev) => ({...prev, entries, values: getInitialValues(entries)}));
    };

    const syncEntries = (entries: StageState<TEntry>["entries"]) => {
        // Syncs entries into stage, preserving values for existing/removed entries.
        // - prev.values spread on top preserves edited values and values for removed entries (for re-adding)
        // - getInitialValues provides defaults for new entries
        setStage((prev) => ({
            ...prev,
            entries,
            values: {...getInitialValues(entries), ...prev.values},
        }));
    };

    const setPredictionSelection = (
        selection: StageState<TEntry>["predictionSelection"],
    ) => {
        setStage((prev) => ({...prev, predictionSelection: selection}));
    };

    const updatePredictionPattern = (hash: string | null) => {
        setStage((prev) => ({
            ...prev,
            predictionSelection: prev.predictionSelection
                ? {
                      ...prev.predictionSelection,
                      selectedPatternHash: hash,
                  }
                : undefined,
        }));
    };

    const addEntry = (entry: StageState<TEntry>["entries"][number]) => {
        setStage((prev) => {
            const newEntries = [...prev.entries, entry];
            return {
                ...prev,
                entries: newEntries,
                values: getInitialValues(newEntries),
            };
        });
    };

    const removeEntry = (entry: StageState<TEntry>["entries"][number]) => {
        setStage((prev) => {
            const newEntries = prev.entries.filter((e) => e.uuid != entry.uuid);
            return {
                ...prev,
                entries: newEntries,
                values: getInitialValues(newEntries),
            };
        });
    };

    const entriesWithValues = stage.entries.map((entry) =>
        produce(entry, (draftState) => {
            draftState.quantity = stage.values[entry.uuid]?.quantity as string;
            draftState.unit = stage.values[entry.uuid]?.unit ?? draftState.unit;

            for (const ingredient of draftState.expanded_recipe) {
                ingredient.quantity = stage.values[ingredient.uuid]?.quantity as string;
                ingredient.unit = stage.values[ingredient.uuid]?.unit as string;
            }

            const recipeCalculations = calculateMealMetrics({
                entries: draftState.expanded_recipe.filter(
                    (ingredient) =>
                        stage.values[ingredient.uuid]?.isRemoved != true &&
                        stage.values[ingredient.uuid]?.quantity != null,
                ),
            });

            if (draftState.expanded_recipe.length > 0) {
                for (const [metric, metricPg] of getMetrics({useKilojoules: false})
                    .ALL_METRICS) {
                    draftState[metricPg] =
                        recipeCalculations[metric]
                            ?.dividedBy(draftState.container_servings)
                            .toString() ?? null;
                }
            }
        }),
    );

    const single = [entriesWithValues[0], null][0];

    if (single == null) {
        return {
            entryMetrics: <></>,
            hasErrors,
            mealSelector: mealEditor.mealSelector,
            data: stage,
            entries: <></>,
            ingredients: <></>,
            singleEntry: <></>,
            bulkEntries: <></>,
            validateMeal: mealEditor.validateMeal,
            validate: () => null,
            calculations: getRoundedCalculations({}, false),
            setEntries,
            syncEntries,
            entriesWithValues,
            addEntry,
            removeEntry,
            setMethod: () => {},
            bulkEntriesContent: () => <></>,
            setPredictionSelection,
            updatePredictionPattern,
        };
    }

    const mealCalculations = getRoundedCalculations(
        calculateMealMetrics({
            entries: entriesWithValues
                .filter((entry) => stage.values[entry.uuid]?.isRemoved != true)
                .map((entry) => ({
                    entry: entry,
                    quantity: entry.quantity,
                    unit: entry.unit,
                })),
        }),
        useKilojoules,
        props.divideByServings,
    );

    const shouldShowWholeContainer = new Decimal(single.container_servings).greaterThan(
        1,
    );

    const wholeContainer = new Decimal(single.container_servings).times(
        single.units.find((u) => u.unit == single.unit)!.multiplier,
    );
    const hasWholeContainerQuantity =
        (single.quantity as string | null) != null &&
        new Decimal(single.quantity).equals(wholeContainer);

    const bindQuantity = (uuid: UUID) => (quantity: string | null) => {
        setStage((prev) =>
            produce(prev, (draftState) => {
                draftState.values[uuid] = {
                    ...draftState.values[uuid],
                    quantity,
                };
            }),
        );
    };

    const bindUnit =
        (uuid: UUID, units: EntryLike["units"]) => (nextUnitValue: string) => {
            const staged = stage.values[uuid];
            const previousUnit = units.find((u) => u.unit == staged?.unit)!;
            const nextUnit = units.find((u) => u.unit == nextUnitValue)!;
            const previousQuantityMultiplier = previousUnit.multiplier;
            const currentQuantityMultiplier = nextUnit.multiplier;

            const convertedQuantity = new Decimal(staged?.quantity ?? 0)
                .dividedBy(previousQuantityMultiplier)
                .times(currentQuantityMultiplier);

            const isContinuousPreviousUnit =
                [
                    "g",
                    "mg",
                    "mcg",
                    "ml",
                    "fl oz",
                    "oz",
                    "lb",
                    "cup",
                    "tsp",
                    "tbsp",
                ].includes(previousUnit.unit) || previousUnit.unit.includes("cup");
            const isContinuousNextUnit =
                [
                    "g",
                    "mg",
                    "mcg",
                    "ml",
                    "fl oz",
                    "oz",
                    "lb",
                    "cup",
                    "tsp",
                    "tbsp",
                ].includes(nextUnit.unit) || nextUnit.unit.includes("cup");
            const nextQuantityValue =
                isContinuousNextUnit || isContinuousPreviousUnit
                    ? convertedQuantity.toString()
                    : staged?.quantity;

            // TODO: this is where smart logic should go so we don't get lossy conversion from 1 to 28 to 0.999 etc.
            setStage((prev) =>
                produce(prev, (draftState) => {
                    draftState.values[uuid] = {
                        ...draftState.values[uuid],
                        quantity: nextQuantityValue,
                        unit: nextUnit.unit,
                    };
                }),
            );
        };

    const bindRemove = (uuid: UUID) => () => {
        setStage((prev) =>
            produce(prev, (draftState) => {
                draftState.values[uuid] = {
                    ...draftState.values[uuid],
                    isRemoved: !(draftState.values[uuid]?.isRemoved ?? false),
                };
            }),
        );
    };
    const singleEntryCalculations = getRoundedCalculations(
        calculateEntryMetrics({
            entry: single,
            quantity: single.quantity,
            unit: single.unit,
        }),
        useKilojoules,
    );

    const entryMetrics =
        hasHiddenMacros == false ? (
            <EntryMetrics calculations={singleEntryCalculations} />
        ) : null;

    const CardComponent = props.cardComponent ?? EntryCard;

    const bulkEntriesContent = (filter: (entry: TEntry) => boolean) =>
        entriesWithValues.filter(filter).map((entry, index) => (
            <CardComponent
                key={index}
                entry={{entry, quantity: entry.quantity, unit: entry.unit}}
                extra={props.extra?.({
                    uuid: entry.uuid,
                    isRemoved: stage.values[entry.uuid]?.isRemoved ?? false,
                })}
                quantityAndUnitHandler={{
                    unit: {
                        onChange: bindUnit(entry.uuid, entry.units),
                        value: entry.unit,
                        options: entry.units.map((unit) => unit.unit),
                    },
                    quantity: {
                        onChange: bindQuantity(entry.uuid),
                        value: entry.quantity,
                    },
                }}
                removeHandler={{
                    onRemove: bindRemove(entry.uuid),
                    isRemoved: stage.values[entry.uuid]?.isRemoved ?? false,
                }}
                hiddenMacros={hasHiddenMacros}
            />
        ));
    const bulkEntries = props.cardComponent ? (
        <>{bulkEntriesContent(() => true)}</>
    ) : (
        <div>
            <div className="space-y-4">
                {mealEditor.mealSelector}
                <Flush>{bulkEntriesContent(() => true)}</Flush>
            </div>
        </div>
    );

    const singleExpandeRecipe = single.expanded_recipe;

    const singleQuantity =
        (single.quantity as string | null) == null
            ? null
            : new Decimal(single.quantity);
    const multiplier =
        singleQuantity == null || singleQuantity.isZero()
            ? new Decimal(1)
            : new Decimal(1).dividedBy(
                  singleQuantity
                      .dividedBy(single.container_servings)
                      .times(
                          single.units.find((u) => u.unit == single.unit)!.multiplier,
                      ),
              );

    const ingredients = singleExpandeRecipe.length > 0 && (
        <Flush>
            {singleExpandeRecipe.map((ingredient, index) => (
                <EntryCard
                    key={index}
                    entry={{...ingredient}}
                    quantityAndUnitHandler={
                        [].length == 0
                            ? {
                                  unit: {
                                      onChange: bindUnit(
                                          ingredient.uuid,
                                          ingredient.units,
                                      ),
                                      value: ingredient.unit,
                                      options: ingredient.units.map(
                                          (unit) => unit.unit,
                                      ),
                                  },
                                  quantity: {
                                      multiplier: multiplier.toString(),
                                      onChange: bindQuantity(ingredient.uuid),
                                      value: ingredient.quantity,
                                  },
                              }
                            : undefined
                    }
                    removeHandler={{
                        onRemove: bindRemove(ingredient.uuid),
                        isRemoved: stage.values[ingredient.uuid]?.isRemoved ?? false,
                    }}
                    hiddenMacros={hasHiddenMacros}
                />
            ))}
        </Flush>
    );

    const unitSelector =
        single.units.length < 6 && single.additional_units.length == 0 ? (
            <QuickSelect
                name=""
                type="text"
                options={single.units.map((unit) => ({
                    value: unit.unit,
                    label: formatUnit(unit.unit),
                }))}
                onChange={(nextUnit) =>
                    nextUnit != null
                        ? bindUnit(single.uuid, single.units)(nextUnit)
                        : null
                }
                value={single.unit}
                shrinkLast={false}
            />
        ) : (
            <widgets.SelectText
                name="unit"
                ariaLabel="unit"
                value={single.unit}
                onChange={(nextUnit) => {
                    bindUnit(single.uuid, single.units)(nextUnit);
                }}
            >
                {single.units.map((unit) => (
                    <option key={unit.unit} value={unit.unit}>
                        {formatUnit(unit.unit)}
                    </option>
                ))}
            </widgets.SelectText>
        );

    const singleEntry = (
        <div className="space-y-4">
            <Label label={null} error={errors.quantity ?? null}>
                <h4 className="text-2xl font-light">{single.name}</h4>
                <div className={styles.quantityUnitAndWholeThing}>
                    <DoubleWidget
                        lopsided={{left: 1, right: 2}}
                        left={
                            <DecimalInput
                                name="quantity"
                                ariaLabel="quantity"
                                onChange={bindQuantity(single.uuid)}
                                value={single.quantity}
                            />
                        }
                        right={unitSelector}
                    />
                    {shouldShowWholeContainer === true && (
                        <typography.Button
                            variant="outline"
                            disabled={hasWholeContainerQuantity}
                            onClick={() => {
                                void analytics.track("CLICKED_WHOLE_CONTAINER", {
                                    whole_container_unit: single.unit,
                                });
                                bindQuantity(single.uuid)(wholeContainer.toString());
                            }}
                            expand
                        >
                            I ate the whole thing
                        </typography.Button>
                    )}
                </div>
            </Label>
            {mealEditor.mealSelector}
            {entryMetrics}
        </div>
    );

    const validate = () => {
        const validatedMeal = mealEditor.validateMeal();
        let hasError = false;

        if (
            entriesWithValues.length == 1 &&
            (entriesWithValues[0].quantity as string | null) == null
        ) {
            setErrors({...errors, quantity: "A quantity is required"});
            hasError = true;
        }
        if (validatedMeal === false || hasError) {
            return null;
        }

        return {
            meal: validatedMeal,
            entries: entriesWithValues
                .filter(
                    (staged) =>
                        stage.values[staged.uuid]?.isRemoved != true &&
                        stage.values[staged.uuid]?.quantity != null,
                )
                .map((e) => ({
                    ...e,
                    expanded_recipe: e.expanded_recipe.filter(
                        (ingredient) =>
                            stage.values[ingredient.uuid]?.isRemoved != true &&
                            stage.values[ingredient.uuid]?.quantity != null,
                    ),
                })),
            single: produce(single, (draftState) => {
                draftState.expanded_recipe = draftState.expanded_recipe.filter(
                    (ingredient) =>
                        stage.values[ingredient.uuid]?.isRemoved != true &&
                        stage.values[ingredient.uuid]?.quantity != null,
                );
            }),
        };
    };

    return {
        entryMetrics,
        mealSelector: mealEditor.mealSelector,
        data: stage,
        entries: stage.entries.length > 1 ? bulkEntries : singleEntry,
        singleEntry,
        bulkEntries,
        ingredients,
        hasErrors,
        validateMeal: mealEditor.validateMeal,
        validate,
        calculations: mealCalculations,
        setEntries,
        syncEntries,
        entriesWithValues,
        addEntry,
        removeEntry,
        setMethod: (method) => {
            setStage((prev) => ({...prev, method}));
        },
        bulkEntriesContent,
        setPredictionSelection,
        updatePredictionPattern,
    };
}
